// @flow
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import {
  get,
  find,
  bindAll,
  isEqual,
  every,
  slice,
  findIndex,
  noop,
  attempt
} from "lodash";
import type {
  RaceProgram,
  BettingInterest,
  RaceWagerType,
  HandicapOptionsType,
  HandicapSortOptionsType,
  ExpandLineArgs,
  RaceListItem,
  RaceCardBettingInterest
} from "@tvg/types/Race";
import { handicapping, sorting } from "@tvg/atomic-ui/_static/Icons/icons";
import handicappingOptions, {
  handicappingGreyhoundOptions
} from "@tvg/utils/handicapping-conf/handicappingOptions";
import type { BetType } from "@tvg/types/Bet";
import type { BetStateType } from "@tvg/atomic-ui/_templates/BetConfirmation/types";
import WagerTypeSelector from "@tvg/atomic-ui/_templates/WagerTypeSelector";
import RaceCard from "@tvg/atomic-ui/_templates/RaceCard";
import buildColor from "@tvg/atomic-ui/_static/ColorPalette";

import mediator from "@tvg/mediator";
import { type TalentPickData } from "@tvg/types/TalentPicks";
import {
  setPickAction,
  setApplyPickAction,
  setBetTicketState,
  setSelectionRequest,
  resetSelection,
  setBetConfirmation,
  setBetState,
  setStartBetting,
  setBetType,
  setBetAmount,
  setPickLeg,
  setSelectionScratched,
  type Actions as BetActions
} from "@tvg/shared-actions/BetActions";
import {
  setHandicappingSortModalStateAction,
  setHandicappingModalStateAction,
  setHandicappingModeAction,
  setHandicappingSortModeAction,
  showMainWagerType,
  userSetHandicappingModeAction,
  userSetHandicappingSortModeAction,
  type Actions as ProgramActions
} from "@tvg/shared-actions/ProgramActions";
import type { NullaryFn, UnaryFn } from "@tvg/types/Functional";
import performanceUtils from "@tvg/utils/performanceUtils";
import BetSelectionUtils from "@tvg/utils/betSelectionUtils";
import RaceHeaderUtils from "@tvg/utils/RaceHeaderUtils";
import { createGTMTag, createGTMLabel } from "@tvg/utils/gtmUtils";
import getRunnersInfoForRaceCard, {
  getHandicappingMode,
  isFirstElementBold
} from "@tvg/utils/handicappingUtils";
import parseCAPIMessage from "@tvg/utils/capiUtils";
import tvgConf from "@tvg/conf";
import IPP from "@tvg/ipp";
import type { HandicappingRunnerFlags } from "@tvg/atomic-ui/_molecule/RunnerFlags";

import type { WagerType } from "@tvg/types/Wager";
import { closeVideo, type Actions as VideoActions } from "../actions/video";

/*
  100 Quinella Wheel
  130 Exacta Wheel
  180 Trifecta Wheel
  230 Superfecta Wheel
  280 Superhighfive Wheel
  880 Omni Wheel
  910 Trio Wheel
 */
const notAllowedBets = [100, 130, 180, 230, 280, 880, 910];

export type Props = {
  dispatch: Dispatch<ProgramActions | BetActions | VideoActions>,
  betState: BetStateType,
  betSelections: string[][],
  currentRace: RaceProgram,
  races: RaceProgram[],
  fcpClient: mixed,
  behgClient: mixed,
  raceNumber: number,
  shouldUpdate: boolean,
  startBetting: boolean,
  isVideoShown: boolean,
  betType: ?WagerType,
  betAmount: number,
  betLeg: number,
  userData: {
    user: { accountNumber: number },
    logged: boolean,
    preferences: { [string]: string },
    gotPreferences: boolean
  },
  handicappingMode: HandicapOptionsType,
  handicappingSortMode: HandicapSortOptionsType,
  isHandicappingModalOpen: boolean,
  isHandicappingSortModalOpen: boolean,
  fixWagerSelector: {
    isWagerSelectorFixed: boolean,
    fixedOffset: number
  },
  talentId: string,
  fixHandicapSection: {
    isHandicapFixed: boolean,
    fixedOffset: number,
    handicapFormatHeight: number
  },
  columnLayout: "single" | "dual",
  talentPick: ?TalentPickData,
  applyPick: boolean,
  renderRaceAlerts: NullaryFn<mixed>,
  showMasterPickTag: boolean,
  userBetPreferences: boolean,
  raceChangesConditions: boolean,
  inLinePastPerformance: boolean,
  expandedRunnerId: string,
  showMainWagerType: boolean,
  handicappingSort: boolean,
  isGreyhound: boolean,
  handicappingInfo: boolean,
  userSetHandicappingMode: ?HandicapOptionsType,
  userSetHandicappingSortMode: ?HandicapSortOptionsType,
  handicappingFavoriteMode: ?HandicapOptionsType,
  handicappingSortFavoriteMode: ?HandicapSortOptionsType,
  selectedRunner: string,
  defaultBetType: string,
  handicappingRunnerFlags: HandicappingRunnerFlags,
  scrollRunnerPP: boolean,
  scrollOffset: number,
  useRaceCardModule: boolean,
  picksTabTitle: string
};

type TrackData = {
  bettingInterests: BettingInterest[][],
  racesData: Array<Array<RaceListItem>>
};

export type RaceCardRaces = {
  currentRace: RaceProgram,
  races: RaceProgram[],
  betType: ?WagerType,
  raceNumber: number
};

const getStartIndex = (races: RaceProgram[], column: number) => {
  // get index of the 1st race
  const startIndex = findIndex(races, (race) => column === +race.raceNumber);
  return startIndex < 0 ? 0 : startIndex;
};

export const getWagerSelectorFixedOffset = (
  isVideoShown: boolean,
  columnLayout: "single" | "dual",
  hasPromos: boolean = false,
  isRaceHighlighted: boolean = false
): number => {
  let dualOffset = 86;

  if (hasPromos) {
    dualOffset = 112;
  }

  if (isRaceHighlighted && !hasPromos) {
    dualOffset = 102;
  }

  let fixedOffset = columnLayout === "single" ? 48 : dualOffset;

  if (
    columnLayout === "single" &&
    isVideoShown &&
    typeof window !== "undefined"
  ) {
    fixedOffset = (window.innerWidth * 9) / 16;
  }

  return fixedOffset > 284 && columnLayout === "single" ? 284 : fixedOffset;
};

export const isHongKongRaces = (code: string) =>
  code.includes("HKS") || code.includes("HKH");

export const getRaceCardRaces = (props: RaceCardRaces): TrackData => {
  const numberOfLegs = get(props, "betType.legCount");
  if (numberOfLegs > 1) {
    const races = get(props, "races", []);

    // get index of the 1st race
    const startIndex = getStartIndex(races, props.raceNumber);

    const legs = slice(races, startIndex, startIndex + numberOfLegs);

    const trackData = {
      bettingInterests: [],
      racesData: []
    };

    if (isHongKongRaces(get(props, "currentRace.track.trackCode", ""))) {
      legs.forEach((race: RaceProgram) => {
        trackData.bettingInterests.push(
          BetSelectionUtils.filterHKCoupledEntries(
            get(race, "bettingInterests", [])
          )
        );
        trackData.racesData.push(RaceHeaderUtils.createRaceDataForHeader(race));
      });
    } else {
      legs.forEach((race: RaceProgram) => {
        trackData.bettingInterests.push(get(race, "bettingInterests", []));
        trackData.racesData.push(RaceHeaderUtils.createRaceDataForHeader(race));
      });
    }

    return trackData;
  }

  return {
    bettingInterests: [
      isHongKongRaces(get(props, "currentRace.track.trackCode", ""))
        ? BetSelectionUtils.filterHKCoupledEntries(
            get(props, "currentRace.bettingInterests", [])
          )
        : get(props, "currentRace.bettingInterests", [])
    ],
    racesData: [RaceHeaderUtils.createRaceDataForHeader(props.currentRace)]
  };
};

export const getBetableRunners = (
  bettingInterests: BettingInterest[] = []
): string[] => {
  const noScratchedRunners = [];

  bettingInterests.forEach((bettingInterest) => {
    if (
      !every(
        get(bettingInterest, "runners"),
        (runner) => runner.scratched === true
      )
    ) {
      noScratchedRunners.push(get(bettingInterest, "biNumber").toString());
    }
  });

  return noScratchedRunners;
};

type State = {
  legExpandedRunner: string[],
  raceNumber: string,
  updateIndex: number
};

export class RaceCardComponent extends Component<Props, State> {
  static defaultProps = {
    betSelections: [[]],
    currentRace: {},
    betState: "none",
    startBetting: false,
    isVideoShown: false,
    betType: null,
    betAmount: 0,
    userData: {
      user: { accountNumber: 0 },
      logged: false,
      preferences: {},
      gotPreferences: false
    },
    isHandicappingModalOpen: false,
    isHandicappingSortModalOpen: false,
    handicappingMode: "basic",
    handicappingSortMode: "saddlecloth",
    fixWagerSelector: { isWagerSelectorFixed: false, fixedOffset: 0 },
    fixHandicapSection: {
      isHandicapFixed: false,
      fixedOffset: 0,
      handicapFormatHeight: 24
    },
    columnLayout: "single",
    talentPick: null,
    applyPick: false,
    renderRaceAlerts: noop,
    showMasterPickTag: false,
    defaultBetType: "",
    userBetPreferences: false,
    raceChangesConditions: false,
    showMainWagerType: true,
    handicappingSort: false,
    isGreyhound: false,
    handicappingInfo: false,
    userSetHandicappingMode: null,
    userSetHandicappingSortMode: null,
    handicappingFavoriteMode: null,
    handicappingSortFavoriteMode: null,
    selectedRunner: "",
    fcpClient: null,
    behgClient: null,
    handicappingRunnerFlags: {
      title: "",
      subtitle: "",
      subtitleRunnerFlags: "",
      flags: []
    },
    scrollRunnerPP: false,
    scrollOffset: 0,
    useRaceCardModule: false,
    picksTabTitle: ""
  };

  constructor(props: Props): void {
    super(props);
    bindAll(this, [
      "selectRunner",
      "selectAllRunners",
      "getListOfWagerTypes",
      "shouldShowBetTicket",
      "updateBetType",
      "selectBetType",
      "getWagerTypeFromList",
      "getCurrentBettingInterests",
      "setPickLeg",
      "callHandicappingModal",
      "onBackToMainWagerTypeSelector",
      "expandLine",
      "expandInfo"
    ]);

    this.state = {
      legExpandedRunner: Array(get(props, "betType.legCount")),
      raceNumber: get(props.currentRace, "raceNumber", "0"),
      updateIndex: 0
    };

    this.props.dispatch(setBetState("none"));
    if (!this.props.talentPick) {
      this.props.dispatch(showMainWagerType(true));
      this.props.dispatch(resetSelection());
      this.props.dispatch(setBetConfirmation(false));
      this.props.dispatch(
        setBetTicketState({
          repeatBetNumber: 1
        })
      );
      this.updateBetType();
    } else {
      this.props.dispatch(showMainWagerType(false));
      this.updateBetType(get(this.props, "betType.type"));
    }
  }

  componentDidMount() {
    this.handleHandicappingModeInfo(false, true);
    window.addEventListener("resize", this.resizeHandler, {
      passive: true
    });
    if (this.props.selectedRunner) {
      this.selectRunner(this.props.selectedRunner);
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    const changedCurrentRace = !isEqual(
      nextProps.currentRace,
      this.props.currentRace
    );
    const changedBetSelections = !isEqual(
      nextProps.betSelections,
      this.props.betSelections
    );
    const changedFixWagerSelector = !isEqual(
      nextProps.fixWagerSelector,
      this.props.fixWagerSelector
    );
    const changedBetState = !isEqual(nextProps.betState, this.props.betState);
    const changedStartBetting = !isEqual(
      nextProps.startBetting,
      this.props.startBetting
    );

    const changedUserSetHandicappingMode =
      this.props.userSetHandicappingMode !== nextProps.userSetHandicappingMode;
    const changedUserSetHandicappingSortMode =
      this.props.userSetHandicappingSortMode !==
      nextProps.userSetHandicappingSortMode;

    const changedBetType = !isEqual(nextProps.betType, this.props.betType);
    const changedBetLeg = !isEqual(nextProps.betLeg, this.props.betLeg);
    const changedUserData = !isEqual(nextProps.userData, this.props.userData);
    const changedHandicappingFavoriteMode =
      nextProps.handicappingFavoriteMode !==
      this.props.handicappingFavoriteMode;
    const changedHandicappingSortFavoriteMode =
      nextProps.handicappingSortFavoriteMode !==
      this.props.handicappingSortFavoriteMode;
    const changedHandicappingModalOpen =
      nextProps.isHandicappingModalOpen !== this.props.isHandicappingModalOpen;
    const changedHandicappingSortModalOpen =
      nextProps.isHandicappingSortModalOpen !==
      this.props.isHandicappingSortModalOpen;
    const changedHandicappingMode =
      nextProps.handicappingMode !== this.props.handicappingMode;
    const changedHandicappingSortMode =
      nextProps.handicappingSortMode !== this.props.handicappingSortMode;
    const changedRaces = !isEqual(nextProps.races, this.props.races);
    const changedIsMainWagerType =
      this.props.showMainWagerType !== nextProps.showMainWagerType;
    const changedFixHandicappingSection = !isEqual(
      this.props.fixHandicapSection,
      nextProps.fixHandicapSection
    );
    const changeApply = nextProps.applyPick !== this.props.applyPick;
    const componentShouldUpdate =
      nextProps.shouldUpdate !== this.props.shouldUpdate;

    const isStateChanged = !isEqual(this.state, nextState);

    return (
      nextProps.shouldUpdate &&
      (componentShouldUpdate ||
        isStateChanged ||
        changedCurrentRace ||
        changeApply ||
        changedBetSelections ||
        changedFixWagerSelector ||
        changedBetState ||
        changedStartBetting ||
        changedBetType ||
        changedBetLeg ||
        changedUserData ||
        changedUserSetHandicappingMode ||
        changedUserSetHandicappingSortMode ||
        changedHandicappingModalOpen ||
        changedHandicappingSortModalOpen ||
        changedHandicappingFavoriteMode ||
        changedHandicappingSortFavoriteMode ||
        changedHandicappingMode ||
        changedHandicappingSortMode ||
        changedRaces ||
        changedIsMainWagerType ||
        changedFixWagerSelector ||
        changedFixHandicappingSection)
    );
  }

  static getDerivedStateFromProps(nextProps: Props, prevState: State) {
    const raceNumber = get(nextProps.currentRace, "raceNumber", "0");

    if (raceNumber !== prevState.raceNumber) {
      return {
        legExpandedRunner: Array(get(nextProps, "betType.legCount")),
        raceNumber
      };
    }

    return null;
  }

  componentDidUpdate(prevProps: Props) {
    // reset main wager type on race change
    if (
      get(prevProps, "currentRace.id", "") !==
        get(this.props, "currentRace.id", "") ||
      !this.props.betType
    ) {
      this.props.dispatch(showMainWagerType(true));
    }

    this.handleHandicappingModeInfo(false, false, prevProps);
    // Update bet type if no bet type is select or if some props change and didn't start betting
    if (
      (((!prevProps.userData.logged && this.props.userData.logged) ||
        !isEqual(
          get(prevProps, "userData.preferences.bet_prefs"),
          get(this.props, "userData.preferences.bet_prefs")
        ) ||
        !isEqual(prevProps.userBetPreferences, this.props.userBetPreferences) ||
        get(prevProps, "currentRace.id", "") !==
          get(this.props, "currentRace.id", "")) &&
        !this.props.startBetting) ||
      !this.props.betType
    ) {
      this.updateBetType();
    }

    // Force update for talent pick
    if (
      get(this.props, "betType.type") &&
      !get(this.props, "betType.columnCount")
    ) {
      this.updateBetType(get(this.props, "betType.type"));
    }

    if (this.props.betType && !this.props.betAmount) {
      this.setDefaultBetAmount(this.props.betType);
    }

    if (
      this.props.betType &&
      !this.props.betAmount &&
      !isEqual(
        get(prevProps, "userData.preferences.bet_prefs"),
        get(this.props, "userData.preferences.bet_prefs")
      )
    ) {
      this.setDefaultBetAmount(this.props.betType);
    }

    if (this.props.applyPick) {
      this.runTalentPickSelectionsCheck();
    }

    if (
      get(this.props, "betType.legCount") ===
        get(prevProps, "betType.legCount") &&
      get(this.props, "currentRace.id") === get(prevProps, "currentRace.id")
    ) {
      const bettingInts = getRaceCardRaces(this.props).bettingInterests;
      const prevBettingInts = getRaceCardRaces(prevProps).bettingInterests;

      if (
        bettingInts.join().length === prevBettingInts.join().length &&
        !isEqual(bettingInts, prevBettingInts)
      ) {
        const diffArr = [];
        const newSelection = [];

        for (let i = 0; i < get(this.props, "betType.columnCount"); i += 1) {
          newSelection.push([]);
        }

        for (let i = 0; i < get(this.props, "betType.legCount"); i += 1) {
          diffArr.push([]);
        }

        try {
          bettingInts.forEach((bettingInt, indexTop) =>
            bettingInt.forEach((bi, indexMed) =>
              bi.runners.forEach((runner, ind) => {
                const isScratched = get(
                  prevBettingInts,
                  `[${indexTop}][${indexMed}].runners[${ind}].scratched`
                );
                const diffArrayIndex =
                  get(this.props, "betType.legCount") > 1 ? indexTop : 0;
                return (
                  runner.scratched !== isScratched &&
                  diffArr[diffArrayIndex].push(runner.runnerId)
                );
              })
            )
          );
        } catch (e) {
          console.error(e);
        }
        this.props.betSelections.forEach((selections, index: number) =>
          selections.forEach(
            (selection) =>
              !diffArr[
                get(this.props, "betType.legCount") > 1 ? index : 0
              ].includes(selection.toString()) &&
              newSelection[index].push(selection)
          )
        );

        if (
          newSelection.join().length !== this.props.betSelections.join().length
        ) {
          // set new selections
          this.props.dispatch(setSelectionRequest(newSelection));
          this.props.dispatch(setStartBetting());
          // set scratched runners
          this.props.dispatch(setSelectionScratched(diffArr));
        }
      }

      // send runner selection time metric when bet selections changes
      if (
        !isEqual(
          get(prevProps, "betSelections"),
          get(this.props, "betSelections")
        )
      ) {
        performanceUtils.measure("runner_selection");
        performanceUtils.measure("runner_selection_all");
      }

      // make sure that the bet type is available on race
      if (
        get(this.props, "betType.type.id") &&
        this.props.currentRace &&
        !BetSelectionUtils.hasBetType(
          get(this.props, "betType.type.id"),
          this.props.currentRace
        )
      ) {
        this.updateBetType();
      }
    }
  }

  componentWillUnmount() {
    this.handleHandicappingModeInfo(true);
    window.removeEventListener("resize", this.resizeHandler, false);
  }

  onBackToMainWagerTypeSelector(): void {
    this.props.dispatch(showMainWagerType(true));

    mediator.base.dispatch({ type: "PPM_BET_TYPE:GO_BACK" });
  }

  getHandicappingFormat(handicapKey: HandicapOptionsType) {
    const option =
      get(this.props, "currentRace.type.code") === "G"
        ? handicappingGreyhoundOptions
        : handicappingOptions;

    const findRes =
      option.find((handicapItem) => handicapItem.key === handicapKey) ||
      option[0];

    return handicapKey === "basic" ? { title: findRes.title } : findRes;
  }

  setDefaultBetAmount(wt: ?RaceWagerType | ?WagerType) {
    const useCustomBetAmount = get(
      this.props,
      "userData.preferences.bet_prefs.useCustomBetAmount"
    );
    const picksDefault = [310, 330, 340, 350, 360, 370, 380, 390, 400];

    if (
      !isHongKongRaces(get(this.props, "currentRace.track.trackCode", "")) &&
      this.props.userBetPreferences &&
      useCustomBetAmount &&
      this.props.userData.logged
    ) {
      const betTypeId = +get(wt, "type.id", "");
      const betTypeGroupId = get(wt, "group.id", "");

      let defaultValue = 0;

      // Identify picks and daily doubles
      if (picksDefault.includes(betTypeId)) {
        defaultValue = +get(
          this.props,
          "userData.preferences.bet_prefs.default_bet_amount.P",
          0
        );
      } else {
        defaultValue = +get(
          this.props,
          `userData.preferences.bet_prefs.default_bet_amount["${betTypeGroupId}"]`,
          0
        );
      }

      this.props.dispatch(
        setBetAmount(
          defaultValue >= get(wt, "minWagerAmount", 100000)
            ? defaultValue
            : get(wt, "wagerAmounts[0]", 0)
        )
      );
    } else {
      this.props.dispatch(setBetAmount(get(wt, "wagerAmounts[0]", 0)));
    }
  }

  getBettableRunnersOnSelections = (index: number): string[] => {
    const bettableRaceRunners = getBetableRunners(
      this.getCurrentBettingInterests(index)
    );
    return bettableRaceRunners.filter(
      (bettableRunner: string): boolean =>
        get(this.props, `betSelections[${index}]`, []).indexOf(bettableRunner) >
        -1
    );
  };

  getListOfWagerTypes(): BetType[] {
    const racesWagerTypes = get(this.props, "currentRace.wagerTypes", []);
    return racesWagerTypes
      .map((wagerType) => ({
        type: wagerType.type,
        group: wagerType.group,
        legCount: wagerType.legCount,
        isKey: wagerType.isKey,
        isWheel: wagerType.isWheel,
        isBox: wagerType.isBox
      }))
      .filter((wt) => notAllowedBets.indexOf(wt.type.id) === -1);
  }

  getWagerTypeFromList(wagerType: ?WagerType): ?RaceWagerType {
    const id = get(wagerType, "id");
    return find(
      get(this.props, "currentRace.wagerTypes", []),
      (wt) => wt.type.id === id
    );
  }

  getCurrentBettingInterests(column: number) {
    if (get(this.props, "betType.legCount") > 1) {
      return get(
        this.props,
        `races[${
          getStartIndex(this.props.races, this.props.raceNumber) + column
        }].bettingInterests`
      );
    }
    return get(this.props, "currentRace.bettingInterests");
  }

  setPickLeg(leg: number = 0) {
    this.props.dispatch(setPickLeg(leg));
  }

  handleHandicappingModeInfo = (
    unmount: boolean,
    mount: boolean = false,
    prevProps?: Props
  ) => {
    const defaultFavMode = "basic";
    const defaultFavSortMode = "saddlecloth";

    const {
      dispatch,
      userData,
      handicappingInfo,
      handicappingMode,
      userSetHandicappingMode,
      userSetHandicappingSortMode,
      handicappingFavoriteMode
    } = this.props;

    const { logged, gotPreferences, preferences } = userData;

    const propChanged = (key: string): boolean =>
      prevProps ? get(this.props, key) !== get(prevProps, key) : false;

    const getFavoritesFromPreferences = (
      handicappingModeValue: ?HandicapOptionsType
    ) => {
      const parsedPreferences = attempt(() =>
        JSON.parse(get(preferences, "handicappingSortFavoriteMode", {}))
      );
      const favMode = get(
        preferences,
        "handicappingFavoriteMode",
        defaultFavMode
      );
      const keyToUse = handicappingModeValue || favMode;

      return {
        favMode,
        favSortMode: parsedPreferences[keyToUse] || defaultFavSortMode
      };
    };

    const changedRace = propChanged("currentRace.id");
    const userLoggedInOut = propChanged("userData.logged");
    const handicappingModeChanged = propChanged("handicappingMode");
    const handicappingSortModeChanged = propChanged("handicappingSortMode");
    const userSetHandicappingModeChanged = propChanged(
      "userSetHandicappingMode"
    );
    const userSetHandicappingSortModeChanged = propChanged(
      "userSetHandicappingSortMode"
    );
    const gotPreferencesChanged = propChanged("userData.gotPreferences");

    const maySetDefaults = logged && gotPreferences;
    const whenToRunThisUpdate =
      handicappingInfo &&
      maySetDefaults &&
      (mount ||
        unmount ||
        changedRace ||
        userLoggedInOut ||
        gotPreferencesChanged ||
        handicappingModeChanged ||
        handicappingSortModeChanged ||
        userSetHandicappingModeChanged ||
        userSetHandicappingSortModeChanged);

    const handicappingModeDidNotChangeToFavorite =
      handicappingMode !== handicappingFavoriteMode;

    const userDidNotChangeOptions =
      !userSetHandicappingMode && !userSetHandicappingSortMode;
    const userSetHandicappingSortModeErased = prevProps
      ? prevProps.userSetHandicappingSortMode && !userSetHandicappingSortMode
      : false;

    if (whenToRunThisUpdate) {
      if (unmount) {
        dispatch(setHandicappingModeAction(defaultFavMode));
        dispatch(setHandicappingSortModeAction(defaultFavSortMode));
        return;
      }

      if (
        mount ||
        changedRace ||
        (gotPreferencesChanged && userDidNotChangeOptions)
      ) {
        const { favMode, favSortMode } = getFavoritesFromPreferences();
        dispatch(setHandicappingModeAction(favMode));
        dispatch(setHandicappingSortModeAction(favSortMode));
        return;
      }
      if (
        (handicappingModeChanged &&
          handicappingModeDidNotChangeToFavorite &&
          !userSetHandicappingSortModeErased) ||
        (userSetHandicappingMode && gotPreferencesChanged)
      ) {
        const { favSortMode } = getFavoritesFromPreferences(
          userSetHandicappingMode || handicappingMode
        );
        dispatch(setHandicappingSortModeAction(favSortMode));
        if (userSetHandicappingMode) {
          dispatch(userSetHandicappingModeAction(null));
        }
        return;
      }

      if (userSetHandicappingSortMode && gotPreferencesChanged) {
        const { favMode } = getFavoritesFromPreferences();
        dispatch(setHandicappingModeAction(favMode));
        dispatch(userSetHandicappingSortModeAction(null));
      }
    }
  };

  resizeHandler = () => {
    setTimeout(() => {
      this.setState((prevState) => ({
        updateIndex: prevState.updateIndex + 1
      }));
    }, 300);
  };

  runTalentPickSelectionsCheck = () => {
    const hasFullBetType = get(this.props, "betType.legCount", false);

    // Case only applies to pre-filled bets from picks
    if (this.props.talentPick && hasFullBetType) {
      const bettableRunners = [this.getBettableRunnersOnSelections(0)];
      const currentWagerType = this.getWagerTypeFromList(
        get(this.props, "betType.type")
      );
      const isMultiLeggedSelectionAwaitingGraphData =
        get(this.props, "betType.legCount", 1) > 1 &&
        this.props.races.filter((race) => !!race.bettingInterests).length < 2;

      this.props.dispatch(showMainWagerType(false));

      this.props.betSelections.forEach((selection: string[], index: number) => {
        const legCountHigherThanOne =
          get(currentWagerType, "legCount") > 1 && index > 0;
        const columnCountHigherThanOne =
          get(currentWagerType, "columnCount") > 1 && index > 0;

        // get betable runners multiple race bets
        if (legCountHigherThanOne || columnCountHigherThanOne) {
          const runners = this.getBettableRunnersOnSelections(index);
          bettableRunners.push(runners);
        }
      });

      if (!isMultiLeggedSelectionAwaitingGraphData) {
        this.props.dispatch(setSelectionRequest(bettableRunners));
        this.props.dispatch(setApplyPickAction(false));
      }
    }
  };

  updateBetType(wagerType: ?WagerType): void {
    // get default wager type info
    let defaultBetTypeID;
    if (!this.props.selectedRunner) {
      if (!this.props.defaultBetType) {
        defaultBetTypeID = wagerType
          ? ""
          : get(this.props, "userData.preferences.bet_prefs.defaultBetType");
      } else {
        defaultBetTypeID = this.props.defaultBetType;
      }
    } else {
      defaultBetTypeID = "10";
    }

    const selectedBetType = BetSelectionUtils.getDefaultBetType(
      this.props.currentRace,
      defaultBetTypeID,
      (this.props.userBetPreferences && this.props.userData.logged) ||
        !!this.props.defaultBetType
    );

    if (this.props.defaultBetType) {
      this.props.dispatch(setStartBetting(true));
      this.props.dispatch(showMainWagerType(false));
    }

    const selectedBetTypeID = get(selectedBetType, "type.id") || "-1";

    // set selected wagerType
    const wt = this.getWagerTypeFromList(wagerType) || selectedBetType;

    // reset main wagerType
    if (!wagerType && this.props.userData.logged) {
      if (
        this.props.userBetPreferences &&
        selectedBetTypeID === +defaultBetTypeID
      ) {
        this.props.dispatch(showMainWagerType(false));
      } else {
        this.props.dispatch(showMainWagerType(true));
      }
    }

    // revove set talent pick
    let hasTalentPick = !!this.props.talentPick;

    if (
      wagerType &&
      get(wagerType, "id") !== get(this.props, "talentPick.wagerType.id")
    ) {
      this.props.dispatch(setPickAction(null));
      hasTalentPick = false;
    }

    this.props.dispatch(setBetType(wt));
    this.props.dispatch(setPickLeg(0));

    if (!hasTalentPick) {
      this.setDefaultBetAmount(wt);
    }

    // build matrix for the new betType
    BetSelectionUtils.configureProgramPageByBetType(
      get(wt, "columnCount"),
      get(wt, "legCount"),
      get(wt, "type.id"),
      this.props.betSelections,
      this.props.dispatch,
      hasTalentPick
    );
  }

  selectBetType(wagerType: WagerType, groupWagerType: ?WagerType): void {
    this.props.dispatch(showMainWagerType(false));
    if (wagerType.id !== get(this.props, "betType.type.id")) {
      this.updateBetType(wagerType);

      mediator.base.dispatch({
        type: "PPM_SELECT_BET_TYPE",
        payload: {
          primaryType: !groupWagerType ? wagerType.name : groupWagerType.name,
          secondaryType: !groupWagerType ? "" : wagerType.name
        }
      });
    }

    // show hide betTicket
    this.props.dispatch(setStartBetting(true));
  }

  selectAllRunners(column: number = 0) {
    performanceUtils.register("runner_selection_all");
    let columnSelection = [];
    // should not make a select all on key column
    if (get(this.props, "betType.isKey") && column === 0) {
      return;
    }

    // TODO add logic for picks
    const selection = get(this.props, `betSelections[${column}]`);
    let allBetableRunners = getBetableRunners(
      this.getCurrentBettingInterests(column)
    );

    // isKey --- key is founded on betSelections on position 0/0 of matrix
    if (get(this.props, "betType.isKey")) {
      const keySelection = get(this.props, "betSelections[0][0]");
      if (keySelection) {
        allBetableRunners = allBetableRunners.filter(
          (value: string) => value !== keySelection
        );
      }
    }

    // Add all runner on column
    if (allBetableRunners.length !== selection.length) {
      this.props.dispatch(showMainWagerType(false));
      columnSelection = allBetableRunners;
      mediator.base.dispatch({
        type: "PPM_SELECT_ALL_RUNNERS",
        payload: {
          race: get(this.props, "currentRace"),
          betType: get(this.props, "betType.type.code"),
          add: true,
          ...createGTMTag(
            get(this.props, "handicappingMode"),
            get(this.props, "currentRace.isGreyhound")
          )
        }
      });
    }

    // remove all from column
    if (columnSelection.length === 0) {
      mediator.base.dispatch({
        type: "PPM_SELECT_ALL_RUNNERS",
        payload: {
          race: get(this.props, "currentRace"),
          betType: get(this.props, "betType.type.code"),
          add: false,
          ...createGTMTag(
            get(this.props, "handicappingMode"),
            get(this.props, "currentRace.isGreyhound")
          )
        }
      });
    }

    // replace old column with new on the matrix
    const newSelection = this.props.betSelections.slice();
    newSelection[column] = columnSelection;

    this.props.dispatch(setSelectionRequest(newSelection));

    // show hide betTicket

    const hasSelections = BetSelectionUtils.hasSelections(newSelection);
    this.props.dispatch(setStartBetting(hasSelections));
  }

  selectRunner(
    selection: string,
    column: number = 0,
    fromIcon: boolean = true,
    fromTrackmaster: boolean = false,
    runnerNumber?: number
  ) {
    // Hack: to remove with inline PPs - this prevents horse selection on exactas / trifects / up... when clicking anywhere else other than the icon
    if (
      get(this.props, "betType.columnCount") > 1 &&
      get(this.props, "betType.legCount") === 1 &&
      !fromIcon
    ) {
      return;
    }

    performanceUtils.register("runner_selection");
    let columnSelection = [];

    // find selection selection on column
    const findSelection = this.props.betSelections[column].find(
      (runner: string) => runner === selection
    );

    // remove selection from column
    if (findSelection) {
      columnSelection = this.props.betSelections[column].filter(
        (runnerId: string) => runnerId !== selection
      );
      mediator.base.dispatch({
        type: "PPM_RUNNER_SELECTION",
        payload: {
          race: this.props.currentRace,
          ...createGTMLabel(false, fromIcon),
          ...createGTMTag(
            get(this.props, "handicappingMode"),
            get(this.props, "currentRace.isGreyhound"),
            runnerNumber
          ),
          betType: get(this.props, "betType.type.code"),
          module: fromTrackmaster ? "Trackmaster" : "Race Card"
        }
      });
      // add selection to the column
    } else {
      this.props.dispatch(showMainWagerType(false));
      columnSelection = get(this.props, `betSelections[${column}]`).slice();
      columnSelection.push(selection);
      const module = fromTrackmaster ? this.props.picksTabTitle : "Race Card";
      mediator.base.dispatch({
        type: "PPM_RUNNER_SELECTION",
        payload: {
          race: this.props.currentRace,
          ...createGTMLabel(true, fromIcon),
          ...createGTMTag(
            get(this.props, "handicappingMode"),
            get(this.props, "currentRace.isGreyhound"),
            runnerNumber
          ),
          betType: get(this.props, "betType.type.code"),
          module: this.props.useRaceCardModule ? `Picks - ${module}` : module
        }
      });
    }

    // reorder column
    columnSelection = columnSelection.sort();
    // replace old column with new on the matrix
    const newSelection = this.props.betSelections.slice();

    // handle keys
    if (get(this.props, "betType.isKey")) {
      if (column === 0) {
        columnSelection = columnSelection.filter(
          (value: string) => value === selection
        );
        newSelection[1] = get(newSelection, "[1]", []).filter(
          (value: string) => value !== selection
        );
      } else {
        newSelection[0] = get(newSelection, "[0]", []).filter(
          (value: string) => value !== selection
        );
      }
    }

    newSelection[column] = columnSelection;

    // save matrix on redux store
    this.props.dispatch(setSelectionRequest(newSelection));

    // show hide betTicket

    const hasSelections = BetSelectionUtils.hasSelections(newSelection);
    this.props.dispatch(setStartBetting(hasSelections));
  }

  isAllSelected(): boolean[] {
    const resIsAllSelected = [];
    let keySelection;

    // isKey get key selected on 1st column
    if (get(this.props, "betType.isKey")) {
      keySelection = get(this.props, "betSelections[0][0]");
    }

    // get betable runners for 1st leg or single race
    let betableRunners = getBetableRunners(this.getCurrentBettingInterests(0));

    this.props.betSelections.forEach((selection: string[], index: number) => {
      // get betable runners multiple race bets
      if (get(this.props, "betType.legCount") > 1 && index > 0) {
        betableRunners = getBetableRunners(
          this.getCurrentBettingInterests(index)
        );
      }

      // if key selected the all selected id the total less 1 position
      if (keySelection) {
        resIsAllSelected.push(selection.length === betableRunners.length - 1);
      } else {
        resIsAllSelected.push(selection.length === betableRunners.length);
      }
    });

    return resIsAllSelected;
  }

  callHandicappingModal() {
    this.props.dispatch(setHandicappingModalStateAction(true));

    mediator.base.dispatch({ type: "HANDICAPPING:OPEN_MODAL" });
  }

  callHandicappingSortModal = () => {
    this.props.dispatch(setHandicappingSortModalStateAction(true));

    mediator.base.dispatch({
      type: "HANDICAPPING_SORT:OPEN_MODAL"
    });
  };

  registerRaceCardBaseEvent = (
    subscribe: boolean,
    type: string,
    callBack: UnaryFn<{ payload: mixed }, void>
  ) => {
    if (subscribe) {
      mediator.base.subscribe(type, callBack);
    } else {
      mediator.base.unsubscribe(type, callBack);
    }
  };

  expandLine(runnerLineInfo: ExpandLineArgs) {
    if (
      this.props.inLinePastPerformance &&
      runnerLineInfo.direction !== "none"
    ) {
      const legExpandedRunner = [...this.state.legExpandedRunner];

      legExpandedRunner[runnerLineInfo.column] =
        legExpandedRunner[runnerLineInfo.column] === runnerLineInfo.runnerId
          ? ""
          : runnerLineInfo.runnerId;

      this.setState(
        {
          legExpandedRunner
        },
        () =>
          this.sendGTMEvent(runnerLineInfo.runnerId, runnerLineInfo.direction)
      );
    } else if (!runnerLineInfo.isScratched) {
      this.selectRunner(
        runnerLineInfo.selection,
        runnerLineInfo.column,
        runnerLineInfo.fromIcon
      );
    }
  }

  expandInfo(
    runnerName: string,
    dob: number,
    runners: RaceCardBettingInterest[],
    entityRunnerId: string
  ) {
    return (
      <IPP
        runnerName={runnerName}
        runnerDob={dob}
        entityRunnerId={entityRunnerId}
        originRaceCode={get(this.props, "currentRace.track.trackCode", "")}
        originRaceNumber={get(this.props, "currentRace.raceNumber", "")}
        currentRace={this.props.currentRace}
        originRaceConditions={{
          distance: get(this.props, "currentRace.distance"),
          surface: get(this.props, "currentRace.surface.shortName")
        }}
        wagerProfile="PORT-Generic"
        fcpClient={this.props.fcpClient}
        behgClient={this.props.behgClient}
        device={tvgConf().device}
        onModalOpen={this.stopVideo}
        runners={runners}
        raceTypeCode={get(this.props, "currentRace.type.code")}
      />
    );
  }

  sendGTMEvent = (runnerId: string, arrowDirection: string) => {
    let expandAction;
    if (arrowDirection === "up") {
      expandAction = "Collapse";
    } else {
      expandAction = "Expand";
    }

    mediator.base.dispatch({
      type: "PAST_PERFORMANCES:EXPAND_COLLAPSE",
      payload: {
        expandAction,
        runnerId
      }
    });
  };

  stopVideo = () => {
    if (this.props.isVideoShown) {
      this.props.dispatch(closeVideo());
    }
  };

  shouldShowBetTicket() {
    if (this.props.startBetting && this.props.betType) {
      mediator.ios.dispatch({
        type: "TOGGLE_BACKGROUND_COLOR",
        payload: {
          backgroundColor: buildColor("white", "100")
        }
      });
    } else {
      mediator.ios.dispatch({
        type: "TOGGLE_BACKGROUND_COLOR",
        payload: {
          backgroundColor: buildColor("blue", "500")
        }
      });
    }
    return !!(this.props.startBetting && this.props.betType);
  }

  render() {
    if (get(this.props, "currentRace.bettingInterests")) {
      const isGreyhound = get(this.props, "currentRace.isGreyhound");
      const legCount = get(this.props, "betType.legCount");
      const trackData = getRaceCardRaces(this.props);
      const handicappingMode = getHandicappingMode(
        this.props.userData.logged,
        this.props.handicappingMode,
        isGreyhound
      );

      const trackDataSource = get(
        this.props.currentRace,
        "track.trackDataSource",
        "EQB"
      );

      const handicapOptions = [
        {
          title: this.getHandicappingFormat(handicappingMode).title,
          callback: this.callHandicappingModal,
          icon: handicapping,
          visible: true,
          qaLabel: "runnerListHeader-handicapInfoBtn"
        },
        {
          title: "Sort",
          callback: this.callHandicappingSortModal,
          icon: sorting,
          visible: !isGreyhound && this.props.handicappingSort,
          qaLabel: "runnerListHeader-handicapSortBtn"
        }
      ];

      const bettingInterests = getRunnersInfoForRaceCard(
        trackData.bettingInterests,
        handicappingMode,
        isGreyhound,
        get(this.props, "currentRace.track.trackCode"),
        this.props.userData.logged && this.props.showMasterPickTag,
        this.props.raceChangesConditions,
        this.props.handicappingMode,
        this.props.handicappingSortMode
      );

      return (
        <Fragment>
          <WagerTypeSelector
            wagerTypes={this.getListOfWagerTypes()}
            active={get(this.props, "betType.type.code")}
            activeRace={this.props.betLeg}
            onSelectWagerType={this.selectBetType}
            onSelectLegRace={this.setPickLeg}
            onBackToMainWagerTypeSelector={this.onBackToMainWagerTypeSelector}
            isMainWagerType={this.props.showMainWagerType}
            raceNumber={this.props.raceNumber}
            fixedOffset={getWagerSelectorFixedOffset(
              this.props.isVideoShown,
              this.props.columnLayout,
              get(this.props, "currentRace.promo.isAboveTheLine", false),
              !!get(this.props, "currentRace.highlighted.description", "")
            )}
            device={tvgConf().device}
          />
          <RaceCard
            key="BetRaceCard"
            bettingInterests={bettingInterests}
            racesData={trackData.racesData}
            raceTypeCode={get(this.props, "currentRace.type.code")}
            isWagerable={get(this.props, "currentRace.wagerable")}
            selections={this.props.betSelections}
            isKey={get(this.props, "betType.isKey")}
            numColumns={legCount > 1 ? 1 : this.props.betSelections.length}
            isAllSelected={this.isAllSelected()}
            onRunnerSelect={this.selectRunner}
            onAllRunnersSelect={this.selectAllRunners}
            selectedLeg={this.props.betLeg}
            onLegSelect={this.setPickLeg}
            handicapType={handicappingMode}
            handicapOptions={handicapOptions}
            legExpandedRunner={this.state.legExpandedRunner}
            expandLine={this.expandLine}
            expandInfo={this.expandInfo}
            inLinePastPerformance={
              !isGreyhound && this.props.inLinePastPerformance
            }
            firstHandicapBold={isFirstElementBold(handicappingMode)}
            fixHandicapSection={this.props.fixHandicapSection}
            hasHandicappingSelection
            renderRaceAlerts={this.props.renderRaceAlerts}
            device={tvgConf().device}
            subscribeBaseEvent={this.registerRaceCardBaseEvent}
            handicappingSortMode={this.props.handicappingSortMode}
            trackDataSource={trackDataSource}
            currentRace={this.props.currentRace}
            handicappingRunnerFlags={this.props.handicappingRunnerFlags}
            scrollRunnerPP={this.props.scrollRunnerPP}
            scrollOffset={this.props.scrollOffset}
          />
        </Fragment>
      );
    }
    return "";
  }
}

export default connect(
  (
    store,
    props: {
      currentRace: RaceProgram,
      races: RaceProgram[],
      raceNumber: number,
      shouldUpdate: boolean,
      columnLayout: "single" | "dual"
    }
  ) => ({
    currentRace: props.currentRace,
    races: props.races,
    isVideoShown: store.RaceProgramVideo.show,
    raceNumber: props.raceNumber,
    columnLayout: props.columnLayout,
    shouldUpdate: props.shouldUpdate,
    userData: store.userData,
    startBetting: store.RaceProgram.startBetting,
    betType: store.RaceProgram.betType,
    betAmount: store.RaceProgram.betAmount,
    fixWagerSelector: store.RaceProgram.fixWagerSelector,
    fixHandicapSection: store.RaceProgram.fixHandicapSection,
    betState: store.RaceProgram.betState,
    betSelections: store.RaceProgram.betSelections,
    isLoading: store.RaceProgramLoading,
    betLeg: store.RaceProgram.betLeg,
    handicappingMode: store.RaceProgram.handicappingMode,
    handicappingSortMode: store.RaceProgram.handicappingSortMode,
    handicappingFavoriteMode: store.RaceProgram.handicappingFavoriteMode,
    handicappingSortFavoriteMode:
      store.RaceProgram.handicappingSortFavoriteMode,
    isHandicappingModalOpen: store.RaceProgram.isHandicappingModalOpen,
    userSetHandicappingMode: store.RaceProgram.userSetHandicappingMode,
    userSetHandicappingSortMode: store.RaceProgram.userSetHandicappingSortMode,
    expandedRunnerId: store.RaceProgram.expandedRunnerId,
    talentPick: store.RaceProgram.talentPick,
    applyPick: store.RaceProgram.applyPick,
    showMainWagerType: store.RaceProgram.showMainWagerType,
    handicappingInfo: get(store, "capi.featureToggles.handicappingInfo", false),
    showMasterPickTag: get(
      store,
      "capi.featureToggles.trackmasterTaginRaceCard",
      false
    ),
    inLinePastPerformance: get(
      store,
      "capi.featureToggles.inLinePastPerformance",
      false
    ),
    userBetPreferences: get(
      store,
      "capi.featureToggles.userBetPreferences",
      false
    ),
    raceChangesConditions: get(
      store,
      "capi.featureToggles.raceChangesConditions",
      false
    ),
    handicappingSort: get(store, "capi.featureToggles.handicappingSort", false),
    handicappingRunnerFlags: parseCAPIMessage(
      store,
      "capi.messages.handicappingRunnerFlags"
    ),
    useRaceCardModule: get(
      store,
      "capi.featureToggles.useRaceCardModule",
      false
    )
  }),
  (dispatch: Dispatch<ProgramActions | BetActions>) => ({ dispatch }),
  null,
  { forwardRef: true }
)(RaceCardComponent);
